import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TravelStories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await axios.get(`${API}/travel-stories`);
      setStories(response.data);
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="w-full" data-testid="travel-stories-page">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/2506923/pexels-photo-2506923.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <motion.div {...fadeInUp}>
            <h1 className="hero-title text-5xl sm:text-6xl lg:text-7xl text-white mb-6" data-testid="page-title">
              Travel Stories
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Real experiences from around the world. What to see, taste, and discover in every destination.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stories Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="stories-grid">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#1E6FBA]" />
          </div>
        ) : (
          <div className="space-y-20">
            {stories.map((story, index) => (
              <motion.div
                key={story.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-12 items-center`}
                data-testid={`story-${story.slug}`}
              >
                <div className="flex-1">
                  <img
                    src={story.hero_image}
                    alt={story.destination}
                    className="w-full h-[400px] object-cover rounded-3xl shadow-lg"
                  />
                </div>

                <div className="flex-1">
                  <h2 className="text-sm font-semibold text-[#1E6FBA] mb-2 uppercase tracking-wider">
                    {story.country}
                  </h2>
                  <h3 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {story.destination}
                  </h3>
                  <p className="text-lg text-[#4A4A4A] mb-6 leading-relaxed">
                    {story.excerpt}
                  </p>

                  <div className="space-y-3 mb-6">
                    <h4 className="font-semibold text-[#1A1A1A]">Must-Experience:</h4>
                    <ul className="space-y-2">
                      {story.experiences.slice(0, 3).map((exp, expIndex) => (
                        <li key={expIndex} className="flex items-start">
                          <ArrowRight className="w-4 h-4 text-[#2E8B57] mr-2 mt-1 flex-shrink-0" />
                          <span className="text-[#4A4A4A]">{exp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link to={`/travel-stories/${story.slug}`}>
                    <button className="btn-scale inline-flex items-center text-[#1E6FBA] hover:text-[#F28A30] font-medium transition-colors" data-testid={`read-more-${story.slug}`}>
                      <span>Read Full Story</span>
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="bg-[#F5E9D4] grain-texture py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ready for Your Own Adventure?
            </h2>
            <p className="text-[#4A4A4A] text-lg mb-6">
              Let us help you plan a trip that becomes your favorite story to tell.
            </p>
            <Link to="/contact">
              <button className="btn-scale bg-[#1E6FBA] hover:bg-[#1558A0] text-white rounded-full px-8 py-4 font-medium" data-testid="cta-plan-trip">
                Plan Your Trip
                <ArrowRight className="inline ml-2 w-5 h-5" />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TravelStories;
